import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const SCROLL_TOLERANCE_PX = 2;

export const useCarousel = ({ itemCount }: { itemCount: number }) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const getCards = useCallback(() => cardRefs.current.filter((card): card is HTMLDivElement => card !== null), []);

  const setCardRef = useCallback((index: number, node: HTMLDivElement | null) => {
    cardRefs.current[index] = node;
  }, []);

  /**
   * Synchronizes React state with the native scroll container. The tolerance
   * avoids flickering controls from sub-pixel scroll values in browsers.
   */
  const syncCarouselState = useCallback(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      setHasOverflow(false);
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }

    const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);

    setHasOverflow(maxScrollLeft > SCROLL_TOLERANCE_PX);
    setCanScrollPrev(viewport.scrollLeft > SCROLL_TOLERANCE_PX);
    setCanScrollNext(viewport.scrollLeft < maxScrollLeft - SCROLL_TOLERANCE_PX);
  }, []);

  useLayoutEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, itemCount);
    syncCarouselState();
  }, [itemCount, syncCarouselState]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const resizeObserver =
      typeof ResizeObserver === 'undefined' || !viewport ? undefined : new ResizeObserver(syncCarouselState);

    if (resizeObserver && viewport) {
      resizeObserver.observe(viewport);
    }
    window.addEventListener('resize', syncCarouselState);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncCarouselState);
    };
  }, [syncCarouselState]);

  /**
   * Moves to the neighboring card and optimistically updates button state so
   * controls feel responsive while the smooth scroll animation is in flight.
   */
  const scrollByOne = useCallback(
    (direction: 1 | -1) => {
      const viewport = viewportRef.current;
      const cards = getCards();

      if (!viewport || cards.length === 0) {
        return;
      }

      const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
      const currentScrollLeft = viewport.scrollLeft;
      const targetScrollLeft =
        direction === 1
          ? Math.min(
              cards.find((card) => card.offsetLeft > currentScrollLeft + SCROLL_TOLERANCE_PX)?.offsetLeft ??
                maxScrollLeft,
              maxScrollLeft,
            )
          : Math.max(
              cards
                .slice()
                .reverse()
                .find((card) => card.offsetLeft < currentScrollLeft - SCROLL_TOLERANCE_PX)?.offsetLeft ?? 0,
              0,
            );

      setCanScrollPrev(targetScrollLeft > SCROLL_TOLERANCE_PX);
      setCanScrollNext(targetScrollLeft < maxScrollLeft - SCROLL_TOLERANCE_PX);
      viewport.scrollTo({
        behavior: 'smooth',
        left: targetScrollLeft,
      });
      window.setTimeout(syncCarouselState, 0);
    },
    [getCards, syncCarouselState],
  );

  const showNext = useCallback(() => {
    scrollByOne(1);
  }, [scrollByOne]);

  const showPrev = useCallback(() => {
    scrollByOne(-1);
  }, [scrollByOne]);

  return {
    canScrollNext,
    canScrollPrev,
    hasOverflow,
    setCardRef,
    showNext,
    showPrev,
    syncCarouselState,
    viewportRef,
  };
};
